import { IHeadlineProperties } from '@vfde-brix/ws10/headline';
import { getNotAvailableHeadline, getPriceSubline } from './textHelper';
import { PreMarketingType } from '../../enums/product';
import {
    IOfferSummaryCardAdditionalPrice,
    IOfferSummaryCardPromoPrice,
    IOfferSummaryCardProperties,
} from '@vfde-brix/ws10/offer-summary-card';
import { AvcResult, Fee, JarvisProduct, PriceData } from '@vfde-sails/avc-core';
import { DeepPartial } from '@vfde-brix/ws10/core/types/Utils';
import { getTariffPriceProps } from './promoPriceHelper';
import { State } from '../../container/PriceSection';
import { Price } from '@vfde-brix/ws10/core';
import { ProductPlaceholder } from '../../enums/placeholder';
import { getCMSConfig } from '../cms';
import { isNotNullOrUndefined } from '../validator';

/**
 * Every possible price labels on Fiber PDP
 */
export enum SummaryCardPriceLabel {
    Monthly = 'Pro Monat',
    Once = 'Einmal',
    Stairway = 'Ab dem $duration. Monat',
    ProvisionFee = 'Internet-Anschlusspreis',
    RouterDeliveryFee = 'Router-Versandkosten',
    InstallationFee = 'Installationspaket einmal',
}

/**
 * Return the updated props for the headline of the Summary Card
 */
export const getUpdatedSummaryCardHeadlineProps = (
    contentEnabled: boolean,
    text?: string,
    preMarketingType?: PreMarketingType,
): Partial<IHeadlineProperties> => ({
    stdContent: contentEnabled
        ? text
        : getNotAvailableHeadline(preMarketingType),
});

/**
 * Return the updated props for the subline of the Summary Card
 */
export const getUpdatedSummaryCardSublineProps = (
    contentEnabled: boolean,
    tariff?: JarvisProduct,
    preMarketingType?: PreMarketingType,
): IOfferSummaryCardProperties['txtSubline'] => [
    getPriceSubline(contentEnabled ? tariff : undefined, preMarketingType),
];

/**
 * Return the updated props for the legal texts of the Summary Card
 */
export const getUpdatedSummaryCardLegalTextProps = (
    tariff: JarvisProduct,
    isFriendsAndFamily: boolean,
): IOfferSummaryCardProperties['txtLegalText'] => {
    const { duration, fnf } = getCMSConfig().offerSummaryCard.legalText;

    return [
        duration.replace(ProductPlaceholder.DURATION, tariff.duration),
        isFriendsAndFamily ? fnf : null,
    ].filter(isNotNullOrUndefined);
};

/**
 * Returns the label for a price based on the price type
 */
const getPriceLabelByType = (price: PriceData) =>
    ('number' === typeof price.monthly)
        ? SummaryCardPriceLabel.Monthly
        : SummaryCardPriceLabel.Once;

/**
 * Returns the price props for the (discounted) stairway price
 */
const getDiscountedStairwayPriceProps = (
    { ff, selectedTariff, avcResult }: State,
): DeepPartial<IOfferSummaryCardPromoPrice | null> => {
    if (!ff || !selectedTariff || !selectedTariff.discount.month) {
        return null;
    }

    const { discount, price } = selectedTariff;
    const regioCharge = avcResult?.selected.regioCharge;
    const priceToShow = +price.monthly + (regioCharge ?? 0);

    return {
        txtLabel: SummaryCardPriceLabel.Stairway
            .replace(ProductPlaceholder.DURATION, `${discount.month + 1}`),
        containerPromoPrice: {
            strikePrice: Price.fromNumber(+priceToShow.toFixed(2)),
            price: Price.fromNumber(+(priceToShow - 5).toFixed(2)),
        },
    };
};

/**
 * Returns the price props of the selceted tariff
 */
const getMainPriceProps = (
    { selectedTariff, ff, avcResult }: State,
): DeepPartial<IOfferSummaryCardPromoPrice | null> => selectedTariff ? {
    txtLabel: getPriceLabelByType(selectedTariff.price),
    containerPromoPrice: getTariffPriceProps(selectedTariff, ff, avcResult?.selected.regioCharge),
} : null;

/**
 * Return the updated props for the prices of the Summary Card
 */
export const getUpdatedSummaryCardPriceProps = (
    state: State,
): DeepPartial<IOfferSummaryCardPromoPrice>[] => state.contentEnabled ? [
    getMainPriceProps(state),
    getDiscountedStairwayPriceProps(state),
].filter(isNotNullOrUndefined) : [];

/**
 * Returns the price props for the (non discounted) stairway additional price
 */
const getStairwayPriceProps = (
    { discount, price }: JarvisProduct,
    ff: boolean,
    avcResult?: AvcResult,
): DeepPartial<IOfferSummaryCardAdditionalPrice> | null => {
    if (!discount.month || ff) {
        return null;
    }

    const regioCharge = avcResult?.selected.regioCharge;
    const priceToShow = +price.monthly + (regioCharge ?? 0);

    return {
        txtLabel: SummaryCardPriceLabel.Stairway
            .replace(ProductPlaceholder.DURATION, `${discount.month + 1}`),
        price: Price.fromNumber(+(priceToShow).toFixed(2)),
    };
};

/**
 * Returns a price prop for a fee if exists
 */
const getFeeProps = (label: string, fee?: Fee): DeepPartial<IOfferSummaryCardAdditionalPrice> | null =>
    (!fee || !fee.price) ? null : {
        txtLabel: label,
        price: Price.fromNumber(fee.price),
    };

/**
 * Returns the discount price props for the additional price
 */
const getTariffDiscountPriceProps = (
    { installationFee, provisionFee }: JarvisProduct,
): DeepPartial<IOfferSummaryCardAdditionalPrice>[] => {
    const { installationFeeTooltip, routerDeliveryFee } = getCMSConfig().offerSummaryCard.additionalPrice;
    const staticRouterDeliveryFee: Fee = {
        price: routerDeliveryFee,
        strikePrice: false,
    };

    return [
        getFeeProps(SummaryCardPriceLabel.ProvisionFee, provisionFee),
        getFeeProps(SummaryCardPriceLabel.RouterDeliveryFee, staticRouterDeliveryFee),
        getFeeProps(
            `${SummaryCardPriceLabel.InstallationFee} ${installationFeeTooltip.icon}`,
            installationFee,
        ),
    ].filter(isNotNullOrUndefined);
};

/**
 * Return the updated props for the additional prices of the Summary Card
 */
export const getUpdatedSummaryCardAddPriceProps = (
    state: State,
): DeepPartial<IOfferSummaryCardAdditionalPrice>[] => state.contentEnabled && state.selectedTariff ? [
    getStairwayPriceProps(state.selectedTariff, state.ff, state.avcResult),
    ...getTariffDiscountPriceProps(state.selectedTariff),
].filter(isNotNullOrUndefined) : [];
